import type { StateCreator } from 'zustand';
import { mindApi, streamMindRequest } from '../services/mindApi';

// ---------------------------------------------------------------------------
// Types (will move to types/index.ts once Cleanup-Agent wires everything)
// ---------------------------------------------------------------------------

export interface MindEntry {
  id: number;
  user_id: number;
  type: 'capture' | 'decision' | 'principle' | 'domain';
  title: string;
  body: string;
  tags: string[];
  domain?: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface QuestionAnalysis {
  blind_spots: string[];
  hidden_assumptions: string[];
  better_versions: {
    version: string;
    why_better: string;
    thinking_level: 'tactical' | 'strategic' | 'meta';
    style: string;
  }[];
  cross_domain_angles: {
    domain: string;
    insight: string;
    analogon: string;
  }[];
  category: 'decision' | 'learning' | 'exploration' | 'problem-solving';
  outlier_question: string;
}

export interface QuestionSession {
  id: number;
  original_question: string;
  deep_cross_domain: boolean;
  analysis: QuestionAnalysis;
  selected_version?: string;
  copied_version?: string;
  category?: string;
  created_at: string;
}

export interface RetrospectiveAnalysis {
  decision_patterns: string[];
  question_trends: { tactical: number; strategic: number; meta: number };
  blind_spots: string[];
  domain_effectiveness: { domain: string; insight_count: number; quality: string }[];
  next_challenges: string[];
}

export interface Retrospective {
  id: number;
  period: string;
  analysis: RetrospectiveAnalysis;
  created_at: string;
}

export interface DashboardData {
  recent_captures: MindEntry[];
  retrospective_due: boolean;
  last_retrospective_at?: string;
  daily_provocation: string;
}

// ---------------------------------------------------------------------------
// Slice types
// ---------------------------------------------------------------------------

export interface MindState {
  entries: MindEntry[];
  questionSessions: QuestionSession[];
  retrospectives: Retrospective[];
  domains: string[];
  dashboardData: DashboardData | null;

  activeStream: 'workshop' | 'challenge' | 'retrospective' | null;
  streamSections: Record<string, string>;
  streamAbortController: AbortController | null;

  mindLoading: boolean;
  captureModalOpen: boolean;
  captureModalPrefill: Partial<MindEntry> | null;
}

export interface MindActions {
  fetchEntries: (filters?: { type?: string; domain?: string; tag?: string; search?: string }) => Promise<void>;
  saveEntry: (entry: { type: string; title: string; body: string; tags?: string[]; domain?: string }) => Promise<number>;
  updateEntry: (id: number, fields: Partial<MindEntry>) => Promise<void>;
  deleteEntry: (id: number) => Promise<void>;
  runWorkshop: (question: string, deepCrossDomain: boolean) => Promise<void>;
  selectVersion: (sessionId: number, version: string, thinkingLevel: string, style: string) => Promise<void>;
  copyVersion: (sessionId: number, version: string) => Promise<void>;
  abortStream: () => void;
  runChallenge: (belief: string) => Promise<void>;
  runRetrospective: (period: string) => Promise<void>;
  fetchDashboard: () => Promise<void>;
  fetchDomains: () => Promise<void>;
  exportMarkdown: () => Promise<string>;
  importMarkdown: (text: string) => Promise<{ created: number; updated: number }>;
  openCaptureModal: (prefill?: Partial<MindEntry>) => void;
  closeCaptureModal: () => void;
}

export type MindSlice = MindState & MindActions;

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createMindSlice: StateCreator<MindSlice> = (set, get) => ({
  // State
  entries: [],
  questionSessions: [],
  retrospectives: [],
  domains: [],
  dashboardData: null,

  activeStream: null,
  streamSections: {},
  streamAbortController: null,

  mindLoading: false,
  captureModalOpen: false,
  captureModalPrefill: null,

  // Actions
  fetchEntries: async (filters) => {
    set({ mindLoading: true });
    try {
      const result = await mindApi.getEntries(filters) as { entries: MindEntry[] };
      set({ entries: result.entries ?? [], mindLoading: false });
    } catch {
      set({ mindLoading: false });
    }
  },

  saveEntry: async (entry) => {
    const result = await mindApi.saveCapture(entry) as { id: number };
    await get().fetchEntries();
    return result.id;
  },

  updateEntry: async (id, fields) => {
    await mindApi.updateEntry(id, fields);
    set((s) => ({
      entries: s.entries.map((e) => (e.id === id ? { ...e, ...fields } : e)),
    }));
  },

  deleteEntry: async (id) => {
    await fetch(`/api/mind/entries/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
      },
    });
    set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
  },

  runWorkshop: async (question, deepCrossDomain) => {
    get().streamAbortController?.abort();
    const controller = new AbortController();
    set({ activeStream: 'workshop', streamSections: {}, streamAbortController: controller });

    try {
      await streamMindRequest(
        '/api/mind/question-workshop',
        { question, deep_cross_domain: deepCrossDomain },
        (section, content) => {
          set((s) => ({
            streamSections: { ...s.streamSections, [section]: content },
          }));
        },
        (_sections) => {
          set({ activeStream: null, streamAbortController: null });
        },
        controller.signal,
      );
    } catch {
      set({ activeStream: null, streamAbortController: null });
    }
  },

  selectVersion: async (sessionId, version, thinkingLevel, style) => {
    await mindApi.selectVersion(sessionId, version, thinkingLevel, style);
  },

  copyVersion: async (sessionId, version) => {
    await mindApi.copyVersion(sessionId, version);
  },

  abortStream: () => {
    get().streamAbortController?.abort();
    set({ activeStream: null, streamSections: {}, streamAbortController: null });
  },

  runChallenge: async (belief) => {
    get().streamAbortController?.abort();
    const controller = new AbortController();
    set({ activeStream: 'challenge', streamSections: {}, streamAbortController: controller });

    try {
      await streamMindRequest(
        '/api/mind/challenge',
        { belief },
        (section, content) => {
          set((s) => ({
            streamSections: { ...s.streamSections, [section]: content },
          }));
        },
        (_sections) => {
          set({ activeStream: null, streamAbortController: null });
        },
        controller.signal,
      );
    } catch {
      set({ activeStream: null, streamAbortController: null });
    }
  },

  runRetrospective: async (period) => {
    get().streamAbortController?.abort();
    const controller = new AbortController();
    set({ activeStream: 'retrospective', streamSections: {}, streamAbortController: controller });

    try {
      await streamMindRequest(
        '/api/mind/retrospective',
        { period },
        (section, content) => {
          set((s) => ({
            streamSections: { ...s.streamSections, [section]: content },
          }));
        },
        (_sections) => {
          set({ activeStream: null, streamAbortController: null });
        },
        controller.signal,
      );
    } catch {
      set({ activeStream: null, streamAbortController: null });
    }
  },

  fetchDashboard: async () => {
    try {
      const result = await mindApi.getDashboard() as DashboardData;
      set({ dashboardData: result });
    } catch {
      // ignore
    }
  },

  fetchDomains: async () => {
    try {
      const result = await mindApi.getDomains() as { domains: string[] };
      set({ domains: result.domains ?? [] });
    } catch {
      // ignore
    }
  },

  exportMarkdown: async () => {
    return mindApi.exportMarkdown();
  },

  importMarkdown: async (text) => {
    const result = await mindApi.importMarkdown(text) as { created: number; updated: number };
    return result;
  },

  openCaptureModal: (prefill) => {
    set({ captureModalOpen: true, captureModalPrefill: prefill ?? null });
  },

  closeCaptureModal: () => {
    set({ captureModalOpen: false, captureModalPrefill: null });
  },
});
